import React, { useState, useEffect, useMemo, useRef } from 'react';
import DialogBox from '../common/DialogBox';
import apiService from '../../services/ApiService';
import { GameDetails, Game } from '../../shared/interfaces';
import { formatDateOnly } from '../../utils/util';
import {
  formatSettlementPartyDisplay,
  getEffectiveSettlementParty,
  isPlSettlementPartyId,
  isSettlementPartyMatch,
  parseSettlementPartyFromApi,
  sortPartiesSettlementLast,
} from '../../utils/matchOverSettlementParty';

// Simple Card wrapper component
const Card: React.FC<{ children: React.ReactNode; className?: string; style?: React.CSSProperties }> = ({ 
  children, 
  className = '', 
  style 
}) => (
  <div 
    className={`rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 border-none shadow-[0_8px_30px_rgb(0,0,0,0.12)] ${className}`}
    style={{ ...style, height: style?.height || '100%', maxHeight: style?.maxHeight || '100%' }}
  >
    {children}
  </div>
);

interface MatchReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  gameId?: number;
  gameDetails?: GameDetails;
  games?: Game[]; // For multiple games scenario
}

interface PartyMatchSummary {
  partyId: number;
  partyCode: string;
  partyName: string;
  totalCredit: number;
  totalDebit: number;
  finalAmount: number;
}

interface MatchReportData {
  game: {
    id: number;
    gameType: string;
    teamA: string;
    teamB: string;
    teamC?: string | null;
    overs?: string | null;
    isGameOver: boolean;
    winningTeam?: string;
    modifiedAt?: string;
  };
  winner: string;
  isGameOver: boolean;
  totalTransactions: number;
  partySummaries: PartyMatchSummary[];
  settlementPartyId?: number;
  settlementPartyCode?: string;
  settlementPartyName?: string;
}

const formatAmount = (value: number): string => {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const MatchReportDialog: React.FC<MatchReportDialogProps> = ({
  isOpen,
  onClose,
  gameId,
  gameDetails,
  games,
}) => {
  const [reportData, setReportData] = useState<MatchReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string>('P&L');
  const leftScrollRef = useRef<HTMLDivElement>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);
  const isMultipleGames = games && games.length > 0;

  useEffect(() => {
    if (isOpen) {
      fetchTenantName();
      if (isMultipleGames) {
        fetchMultipleMatchReports();
      } else if (gameId) {
        fetchMatchReport();
      }
    } else {
    }
  }, [isOpen, gameId, isMultipleGames]);

  const fetchTenantName = async () => {
    try {
      // Get tenant name from profile API
      const profileResponse = await apiService.get('/Auth/profile');
      const profileData = (profileResponse as any).data || profileResponse;
      // tenantName is now available in the Profile API response
      const tenant = profileData.tenantName || localStorage.getItem('tenantName') || 'P&L';
      setTenantName(tenant);
    } catch (err) {
      // Fallback to localStorage or default
      const tenant = localStorage.getItem('tenantName') || 'P&L';
      setTenantName(tenant);
    }
  };

  const fetchMatchReport = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get(`/Game/${gameId}/match-report`);
      
      const data = response.data || response;
      const settlement = parseSettlementPartyFromApi(data as Record<string, unknown>);
      const gameRaw = (data.game ?? {}) as Record<string, unknown>;
      const gameSettlement = parseSettlementPartyFromApi(gameRaw);

      setReportData({
        game: data.game || {
          id: gameId!,
          gameType: gameDetails?.gameType || '',
          teamA: gameDetails?.teamA || '',
          teamB: gameDetails?.teamB || '',
          teamC: gameDetails?.teamC || null,
          isGameOver: true,
          modifiedAt: data.game?.modifiedAt,
        },
        winner: data.winner || '',
        isGameOver: data.isGameOver || false,
        totalTransactions: data.totalTransactions || 0,
        partySummaries: data.partySummaries || [],
        settlementPartyId: settlement.settlementPartyId ?? gameSettlement.settlementPartyId,
        settlementPartyCode: settlement.settlementPartyCode ?? gameSettlement.settlementPartyCode,
        settlementPartyName: settlement.settlementPartyName ?? gameSettlement.settlementPartyName,
      });
      setLoading(false);
    } catch (err: any) {
      console.error('Failed to fetch match report:', err);
      setError(err?.message || 'Failed to load match report');
      
      const fallbackGame = gameDetails || games?.[0];
      setReportData({
        game: {
          id: gameId!,
          gameType: fallbackGame?.gameType || '',
          teamA: fallbackGame?.teamA || '',
          teamB: fallbackGame?.teamB || '',
          teamC: fallbackGame?.teamC || null,
          isGameOver: true,
        },
        winner: '',
        isGameOver: false,
        totalTransactions: 0,
        partySummaries: [],
      });
      setLoading(false);
    }
  };

  const fetchMultipleMatchReports = async () => {
    setLoading(true);
    setError(null);

    try {
      const gameIds = games!.map(game => game.id);
      const response = await apiService.post('/Game/match-report', { gameIds });
      
      const data = response.data || response;
      const settlement = parseSettlementPartyFromApi(data as Record<string, unknown>);

      setReportData({
        game: data.game || {
          id: gameIds[0],
          gameType: games![0]?.gameType || '',
          teamA: games![0]?.teamA || '',
          teamB: games![0]?.teamB || '',
          teamC: games![0]?.teamC || null,
          isGameOver: true,
          modifiedAt: games![0]?.modifiedAt || games![0]?.modifiedOn,
        },
        winner: data.winner || 'Multiple Matches',
        isGameOver: data.isGameOver || false,
        totalTransactions: data.totalTransactions || 0,
        partySummaries: data.partySummaries || [],
        settlementPartyId: settlement.settlementPartyId,
        settlementPartyCode: settlement.settlementPartyCode,
        settlementPartyName: settlement.settlementPartyName,
      });
      
      setLoading(false);
    } catch (err: any) {
      console.error('Failed to fetch multiple match reports:', err);
      setError(err?.message || 'Failed to load match reports');
      setLoading(false);
    }
  };

  // Helper function to process report data
  const processReportData = (
    data: MatchReportData | null,
    settlement: { settlementPartyId?: number | null; settlementPartyCode?: string | null },
  ) => {
    if (!data || !data.partySummaries) {
      return { leftParties: [], rightParties: [], leftTotal: 0, rightTotal: 0, pnlAmount: 0, maxRows: 0 };
    }

    const left: PartyMatchSummary[] = [];
    const right: PartyMatchSummary[] = [];
    let leftSum = 0;
    let rightSum = 0;

    data.partySummaries.forEach(party => {
      // Swapped logic: negative amounts go to Receivable (right), positive amounts go to Owed (left)
      if (party.finalAmount < 0) {
        right.push(party);
        rightSum += Math.abs(party.finalAmount);
      } else if (party.finalAmount > 0) {
        left.push(party);
        leftSum += party.finalAmount;
      }
    });

    const sortedLeft = sortPartiesSettlementLast(left, settlement);
    const sortedRight = sortPartiesSettlementLast(right, settlement);

    // Calculate P&L amount (difference between sides)
    // P&L goes to the side with less total
    const difference = Math.abs(leftSum - rightSum);
    const pnl = leftSum < rightSum ? difference : -difference;

    // Calculate max rows including P&L rows
    const leftHasPnl = leftSum < rightSum && pnl !== 0;
    const rightHasPnl = rightSum < leftSum && pnl !== 0;
    const leftRowCount = sortedLeft.length + (leftHasPnl ? 1 : 0);
    const rightRowCount = sortedRight.length + (rightHasPnl ? 1 : 0);
    const maxRowCount = Math.max(leftRowCount, rightRowCount);

    return {
      leftParties: sortedLeft,
      rightParties: sortedRight,
      leftTotal: leftSum,
      rightTotal: rightSum,
      pnlAmount: pnl,
      maxRows: maxRowCount,
    };
  };

  const settlementMeta = useMemo(
    () => getEffectiveSettlementParty(reportData, gameDetails),
    [reportData, gameDetails],
  );

  // Separate parties: negative amounts go to Receivable (right), positive amounts go to Owed (left)
  const { leftParties, rightParties, leftTotal, rightTotal, pnlAmount, maxRows } = useMemo(() => {
    return processReportData(reportData, settlementMeta);
  }, [reportData, settlementMeta]);

  // Synchronize scrolling between left and right tables
  useEffect(() => {
    const leftScroll = leftScrollRef.current;
    const rightScroll = rightScrollRef.current;

    if (!leftScroll || !rightScroll) return;

    const handleLeftScroll = () => {
      if (rightScroll) {
        rightScroll.scrollTop = leftScroll.scrollTop;
      }
    };

    const handleRightScroll = () => {
      if (leftScroll) {
        leftScroll.scrollTop = rightScroll.scrollTop;
      }
    };

    leftScroll.addEventListener('scroll', handleLeftScroll);
    rightScroll.addEventListener('scroll', handleRightScroll);

    return () => {
      leftScroll.removeEventListener('scroll', handleLeftScroll);
      rightScroll.removeEventListener('scroll', handleRightScroll);
    };
  }, [reportData]);

  const getProfitLossColor = (amount: number) => {
    if (amount < 0) return 'text-[hsl(var(--debit))]';
    if (amount > 0) return 'text-[hsl(var(--credit))]';
    return 'text-[hsl(var(--foreground))]';
  };

  const getProfitLossSymbol = (amount: number) => {
    if (amount > 0) return '+';
    if (amount < 0) return '-';
    return '';
  };

  const renderPartyRow = (party: PartyMatchSummary) => (
    <tr key={party.partyId} className="border-b border-gray-600 hover:bg-gray-700">
      <td className="p-2 text-white">
        <span className="text-white uppercase">{party.partyCode} - {party.partyName}</span>
      </td>
      <td className={`p-2 text-right font-semibold ${getProfitLossColor(party.finalAmount)}`}>
        {getProfitLossSymbol(party.finalAmount)}{formatAmount(Math.abs(party.finalAmount / 100))}
      </td>
    </tr>
  );

  const renderSettlementBadge = () => (
    <span className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-200">
      Settlement
    </span>
  );

  const renderTable = (
    parties: PartyMatchSummary[], 
    total: number, 
    isLeft: boolean, 
    scrollRef: React.RefObject<HTMLDivElement | null>,
    otherParties: PartyMatchSummary[] = [],
    otherTotal: number = 0,
    pnlAmt: number = 0,
    maxRowsCount: number = 0
  ) => {
    // Use provided values or fallback to component-level values
    const leftTotalVal = isLeft ? total : (otherTotal || leftTotal);
    const rightTotalVal = isLeft ? (otherTotal || rightTotal) : total;
    const pnlValue = pnlAmt !== 0 ? pnlAmt : pnlAmount;
    const maxRowsValue = maxRowsCount > 0 ? maxRowsCount : maxRows;
    
    const hasPnl = ((isLeft && leftTotalVal < rightTotalVal) || (!isLeft && rightTotalVal < leftTotalVal)) && pnlValue !== 0;
    const currentRowCount = parties.length + (hasPnl ? 1 : 0);
    const emptyRows = maxRowsValue - currentRowCount;
    const finalTotal = total + (hasPnl ? Math.abs(pnlValue) : 0);
    // P&L on left side should be negative, on right side should be positive
    const pnlDisplayAmount = hasPnl ? (isLeft ? -Math.abs(pnlValue) : Math.abs(pnlValue)) : 0;
    const isDebit = isLeft;
    const isPlSettlement = isPlSettlementPartyId(settlementMeta.settlementPartyId);

    return (
      <Card className="overflow-hidden h-full flex flex-col" style={{ height: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header with Icon */}
        <div className={`py-1.5 px-3 flex-shrink-0 ${isDebit ? 'bg-gradient-to-r from-red-500 via-rose-500 to-red-600' : 'bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-600'}`} style={{ flexShrink: 0 }}>
          <div className="flex items-center justify-center gap-1.5">
            {isDebit ? (
              <svg className="w-4 h-4 text-red-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-emerald-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            )}
            <h2 className={`text-xs font-bold uppercase tracking-wider ${isDebit ? 'text-red-50' : 'text-emerald-50'}`}>
              {isDebit ? 'Owed' : 'Receivable'}
            </h2>
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden" style={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', minHeight: 0, maxHeight: '100%', overflow: 'hidden' }}>
          {/* Table Header - Fixed */}
          <div className="flex-shrink-0 bg-slate-100/50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700" style={{ flexShrink: 0 }}>
            <div className="grid grid-cols-[2fr,1fr] px-4 py-1.5">
              <div className="text-sm font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide">
                Name
              </div>
              <div className="text-sm font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide text-right">
                Amount
              </div>
            </div>
          </div>

          {/* Table Body - Scrollable */}
          <div 
            ref={scrollRef} 
            className="divide-y divide-slate-200 dark:divide-slate-700 flex-1 overflow-y-auto min-h-0 scrollbar-thin scrollbar-thumb-slate-400 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent"
            style={{ 
              flex: '1 1 0', 
              minHeight: 0, 
              maxHeight: '100%', 
              overflowY: 'auto', 
              overflowX: 'hidden', 
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgb(148 163 184) transparent'
            }}
          >
            {parties.length === 0 && !hasPnl ? (
              <div className="text-center p-8 italic text-sm text-slate-600 dark:text-slate-400">No data available</div>
            ) : (
              <>
                {parties.map((party) => {
                  const isSettlement = isSettlementPartyMatch(party, settlementMeta);
                  return (
                  <div
                    key={party.partyId}
                    className={`grid grid-cols-[2fr,1fr] px-4 py-1.5 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 hover:shadow-sm cursor-pointer transition-all duration-200 rounded-sm ${
                      isSettlement ? 'bg-indigo-50/80 dark:bg-indigo-900/30 border-l-4 border-indigo-500' : ''
                    }`}
                  >
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100 uppercase tracking-wide">
                      {party.partyCode} - {party.partyName}
                      {isSettlement && renderSettlementBadge()}
                    </div>
                    <div className={`text-sm font-bold text-right ${isDebit ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                      {isDebit ? '-' : ''}{formatAmount(Math.abs(party.finalAmount / 100))}
                    </div>
                  </div>
                  );
                })}
                {/* Add P&L row if this side has less total */}
                {hasPnl && (
                  <div 
                    className={`grid grid-cols-[2fr,1fr] px-4 py-1.5 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 hover:shadow-sm cursor-pointer transition-all duration-200 rounded-sm border-l-4 ${
                      isPlSettlement
                        ? 'bg-indigo-50/80 dark:bg-indigo-900/30 border-indigo-500'
                        : isDebit
                          ? 'bg-emerald-100/70 dark:bg-emerald-900/40 border-emerald-600'
                          : 'bg-red-100/70 dark:bg-red-900/40 border-red-600'
                    }`}
                  >
                    <div className="text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wide">
                      P&L - {tenantName}
                      {isPlSettlement && renderSettlementBadge()}
                    </div>
                    <div className={`text-sm font-bold text-right ${isDebit ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                      {isDebit ? '-' : ''}{formatAmount(Math.abs(pnlAmount / 100))}
                    </div>
                  </div>
                )}
                {/* Add empty rows to match the other side */}
                {emptyRows > 0 && Array.from({ length: emptyRows }).map((_, idx) => (
                  <div key={`empty-${idx}`} className="grid grid-cols-[2fr,1fr] px-4 py-1.5 opacity-0 pointer-events-none">
                    <div className="invisible">placeholder</div>
                    <div className="invisible">placeholder</div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Table Footer - Fixed at bottom */}
          <div className="flex-shrink-0 bg-slate-200/90 dark:bg-slate-700/90 border-t-2 border-slate-200 dark:border-slate-700 rounded-b-lg" style={{ flexShrink: 0 }}>
            <div className="grid grid-cols-[2fr,1fr] px-4 py-2">
              <div className="text-base font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wide">
                Total
              </div>
              <div className={`text-base font-bold text-right ${isDebit ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                {isDebit ? '-' : ''}{formatAmount(finalTotal / 100)}
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  const isTestMatch = gameDetails?.gameType?.toLowerCase() === 'test' || games?.[0]?.gameType?.toLowerCase() === 'test';

  if (!isOpen) {
    return null;
  }

  return (
    <DialogBox 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Selected Match Detail"
      panelStyle={{ width: '90vw', height: '90vh', minWidth: '90vw', minHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      panelClassName="flex flex-col overflow-hidden"
    >
      <div className="w-full flex-1 bg-white dark:bg-slate-900 flex flex-col overflow-hidden min-h-0" style={{ flex: '1 1 0', minHeight: 0, maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header Card - Scrollable for multiple games */}
        <div className="flex-shrink-0 pt-2 px-2 md:px-4 pb-2" style={{ flexShrink: 0 }}>
          <Card className="overflow-hidden border-none">
            <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-700 py-1 px-3 dark:from-slate-800 dark:via-slate-700 dark:to-slate-800">
              <h1 className="text-sm font-bold text-white text-center tracking-tight">
                Match Report{isMultipleGames ? ` (${games?.length || 0} games)` : ''}
              </h1>
              {isMultipleGames ? (
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-slate-400 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent justify-start pl-1 pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgb(148 163 184) transparent' }}>
                  {games!.map((game) => {
                    const modifiedAt = game.modifiedAt || game.modifiedOn;
                    const modifiedAtStr = modifiedAt ? (typeof modifiedAt === 'string' ? modifiedAt : modifiedAt.toISOString()) : undefined;
                    return (
                      <div key={game.id} className="flex-shrink-0 flex items-center gap-1.5 text-white/90 px-2 py-1 bg-white/10 rounded-lg">
                        <div className="flex items-center gap-1.5 text-xs font-semibold">
                          <span className="px-1.5 py-0.5 bg-white/20 rounded backdrop-blur-sm text-xs">{game.teamA}</span>
                          <span className="text-white/60 text-xs">VS</span>
                          <span className="px-1.5 py-0.5 bg-white/20 rounded backdrop-blur-sm text-xs">{game.teamB}</span>
                          {game.teamC && (
                            <>
                              <span className="text-white/60 text-xs">VS</span>
                              <span className="px-1.5 py-0.5 bg-white/20 rounded backdrop-blur-sm text-xs">{game.teamC}</span>
                            </>
                          )}
                        </div>
                        {modifiedAtStr && (
                          <>
                            <span className="text-white/40 text-xs">•</span>
                            <span className="text-xs whitespace-nowrap">{formatDateOnly(modifiedAtStr) || '—'}</span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : reportData?.game && (
                <div className="flex flex-wrap items-center justify-center gap-1.5 text-white/90">
                  <div className="flex items-center gap-1.5 text-sm font-semibold">
                    <span className="px-2 py-0.5 bg-white/20 rounded-lg backdrop-blur-sm text-xs">{reportData.game.teamA}</span>
                    <span className="text-white/60 text-xs">VS</span>
                    <span className="px-2 py-0.5 bg-white/20 rounded-lg backdrop-blur-sm text-xs">{reportData.game.teamB}</span>
                    {reportData.game.teamC && (
                      <>
                        <span className="text-white/60 text-xs">VS</span>
                        <span className="px-2 py-0.5 bg-white/20 rounded-lg backdrop-blur-sm text-xs">{reportData.game.teamC}</span>
                      </>
                    )}
                  </div>
                  {reportData.game.modifiedAt && (
                    <>
                      <span className="text-white/40 text-xs">•</span>
                      <span className="text-xs">{formatDateOnly(reportData.game.modifiedAt) || '—'}</span>
                    </>
                  )}
                  {reportData.winner && (
                    <>
                      <span className="text-white/40 text-xs">•</span>
                      <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-500/20 rounded-lg backdrop-blur-sm">
                        <svg
                          className="w-3 h-3 text-emerald-300"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-xs font-semibold text-emerald-200">
                          {reportData.winner === 'Tie' || reportData.winner === 'Abandoned'
                            ? reportData.winner
                            : `Winner: ${reportData.winner}`}
                        </span>
                      </div>
                    </>
                  )}
                  {reportData.winner !== 'Tie' &&
                    reportData.winner !== 'Abandoned' &&
                    (reportData.settlementPartyId !== undefined ||
                    reportData.settlementPartyCode ||
                    gameDetails?.settlementPartyId !== undefined) && (
                    <>
                      <span className="text-white/40 text-xs">•</span>
                      <span className="text-xs font-medium text-white/90">
                        Settlement:{' '}
                        {formatSettlementPartyDisplay(
                          settlementMeta.settlementPartyId,
                          settlementMeta.settlementPartyCode,
                          settlementMeta.settlementPartyName,
                          tenantName,
                        )}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden px-2 md:px-4 pb-2" style={{ flex: '1 1 0', minHeight: 0, maxHeight: '100%', overflow: 'hidden', paddingTop: '0.25rem', display: 'flex', flexDirection: 'column' }}>
          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-300 border-t-transparent"></div>
              <span className="ml-3 text-white text-base font-medium">Loading match report...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-900/80 border-2 border-red-500 text-red-100 px-4 py-3 rounded-lg mb-4 text-sm shadow-lg">
              <span className="font-semibold">Error: </span>{error}
            </div>
          )}

          {reportData && !loading && (
            <div className="flex-1 grid md:grid-cols-2 gap-6 overflow-hidden min-h-0" style={{ flex: '1 1 0', minHeight: 0, maxHeight: '100%', display: 'grid', gridTemplateRows: '1fr', overflow: 'hidden' }}>
              {/* Debit Table */}
              <div className="h-full overflow-hidden min-h-0" style={{ height: '100%', minHeight: 0, maxHeight: '100%', overflow: 'hidden' }}>
                {renderTable(leftParties, leftTotal, true, leftScrollRef, rightParties, rightTotal, pnlAmount, maxRows)}
              </div>

              {/* Credit Table */}
              <div className="h-full overflow-hidden min-h-0" style={{ height: '100%', minHeight: 0, maxHeight: '100%', overflow: 'hidden' }}>
                {renderTable(rightParties, rightTotal, false, rightScrollRef, leftParties, leftTotal, pnlAmount, maxRows)}
              </div>
            </div>
          )}

          {!loading && !reportData && !error && (
            <div className="text-gray-300 text-center py-8">
              No report data available
            </div>
          )}
        </div>
      </div>
    </DialogBox>
  );
};

export default MatchReportDialog;
